import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { embedDashboard } from "@superset-ui/embedded-sdk";
import * as Constants from "common/constants";
import { encryptStorage1 } from "common/general";

const SupersetDashboard = () => {
	const { t } = useTranslation();
	const containerRef = useRef(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [searchParams] = useSearchParams();
	const embeddedId = searchParams.get("embeddedId");

	useEffect(() => {
		if (!embeddedId) {
			setError(t("Dashboard not found"));
			setLoading(false);
			return;
		}

		let mounted = true;

		const fetchGuestToken = async () => {
			const accessToken = encryptStorage1.getItem(Constants.AccessTokenKeyInLocalStorage);
			const response = await fetch(Constants.GetSupersetGuestToken + embeddedId, {
				method: "GET",
				headers: {
					Authorization: "Bearer " + accessToken,
					"Content-Type": "application/json",
				},
			});

			if (!response.ok) {
				throw new Error(t("Dashboard not found"));
			}

			const data = await response.json();
			return data;
		};

		const initDashboard = async () => {
			try {
				const { token, supersetDomain, dashboardUiConfig } = await fetchGuestToken();

				if (!mounted) return;

				await embedDashboard({
					id: embeddedId,
					supersetDomain: supersetDomain,
					mountPoint: containerRef.current,
					fetchGuestToken: async () => {
						const data = await fetchGuestToken();
						return data.token;
					},
					dashboardUiConfig: {
						hideTitle: dashboardUiConfig?.hideTitle ?? true,
						hideChartControls: dashboardUiConfig?.hideChartControls ?? true,
						hideTab: dashboardUiConfig?.hideTab ?? false,
						filters: {
							expanded: dashboardUiConfig?.filtersExpanded ?? false,
						},
					},
				});

				if (mounted) {
					setLoading(false);
					// Make the embedded iframe fill the container
					const iframe = containerRef.current?.querySelector("iframe");
					if (iframe) {
						iframe.style.width = "100%";
						iframe.style.height = "100%";
						iframe.style.border = "none";
					}
				}
			} catch (err) {
				if (mounted) {
					setError(err.message || t("Dashboard not found"));
					setLoading(false);
				}
			}
		};

		initDashboard();

		return () => {
			mounted = false;
		};
	}, [embeddedId]);

	if (error) {
		return (
			<div style={{ padding: "20px", textAlign: "center", color: "#dc3545" }}>
				<p>{error}</p>
			</div>
		);
	}

	return (
		<div style={{ width: "100%", height: "calc(100vh - 150px)", position: "relative" }}>
			{loading && (
				<div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
					<div className="spinner-border text-primary" role="status">
						<span className="visually-hidden">Loading...</span>
					</div>
				</div>
			)}
			<div ref={containerRef} style={{ width: "100%", height: "100%" }} />
		</div>
	);
};

export default SupersetDashboard;
